
import React from 'react';
import { GraphTeam } from '@/hooks/useGraphTeams';
import { Card, CardContent } from '@/components/ui/card';
import { Users, ArrowLeft, ArrowRight, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import GraphTeamCard from './GraphTeamCard';

interface TeamCarouselProps {
  teams: GraphTeam[];
  loading: boolean;
  isConfigured: boolean;
}

interface CustomArrowProps {
  onClick?: () => void;
}

const TeamCarousel = ({ teams, loading, isConfigured }: TeamCarouselProps) => {
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array(2).fill(0).map((_, i) => (
            <Card key={i} className="bg-white h-64 animate-pulse rounded-2xl">
              <CardContent className="p-6">
                <div className="w-24 h-6 bg-gray-200 rounded mb-4"></div>
                <div className="w-full h-4 bg-gray-100 rounded mb-2"></div>
                <div className="w-2/3 h-4 bg-gray-100 rounded mb-8"></div>
                <div className="w-full h-40 bg-gray-100 rounded-lg"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!isConfigured) {
    return (
      <Card className="bg-white p-6 text-center rounded-2xl">
        <div className="flex flex-col items-center justify-center">
          <ShieldAlert className="h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold mb-2">Graph API Not Configured</h3>
          <p className="text-gray-500 mb-4">The Microsoft Graph API connection has not been configured properly.</p>
          <Button variant="outline" className="rounded-full">Configure Graph API</Button>
        </div>
      </Card>
    );
  }

  if (teams.length === 0) {
    return (
      <Card className="bg-white p-6 rounded-2xl">
        <div className="flex flex-col items-center justify-center text-center">
          <Users className="h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Teams Found</h3>
          <p className="text-gray-500">Try adjusting your search or filters</p>
        </div>
      </Card>
    );
  }

  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 1536 },
      items: 2
    },
    desktop: {
      breakpoint: { max: 1536, min: 1024 },
      items: 2
    },
    tablet: {
      breakpoint: { max: 1024, min: 640 },
      items: 2
    },
    mobile: {
      breakpoint: { max: 640, min: 0 },
      items: 1
    }
  };

  const CustomRightArrow = ({ onClick }: CustomArrowProps) => (
    <Button 
      variant="outline" 
      size="icon" 
      onClick={onClick} 
      className="absolute right-0 z-10 rounded-full bg-white shadow-md"
      style={{ right: 0, top: '50%', transform: 'translateY(-50%)' }}
    >
      <ArrowRight size={18} />
    </Button>
  );

  const CustomLeftArrow = ({ onClick }: CustomArrowProps) => (
    <Button 
      variant="outline" 
      size="icon" 
      onClick={onClick} 
      className="absolute left-0 z-10 rounded-full bg-white shadow-md"
      style={{ left: 0, top: '50%', transform: 'translateY(-50%)' }}
    >
      <ArrowLeft size={18} />
    </Button>
  );

  return (
    <div className="space-y-6">
      <div className="py-4 px-2">
        <Carousel
          responsive={responsive}
          infinite
          removeArrowOnDeviceType={["mobile"]}
          customRightArrow={<CustomRightArrow />}
          customLeftArrow={<CustomLeftArrow />}
          itemClass="px-3 pb-4"
          containerClass="carousel-container"
        >
          {teams.map((team) => (
            <GraphTeamCard key={team.id} team={team} />
          ))}
        </Carousel>
      </div>
    </div>
  );
};

export default TeamCarousel;
